import React from 'react';
import PropTypes from 'prop-types';
import { useTranslations, useLocale } from 'next-intl';
import TypesenseInstantsearchAdapter from 'typesense-instantsearch-adapter';
import {
  useSearchBox,
  InstantSearch,
  Hits,
  Configure,
  usePagination,
  useInstantSearch
} from 'react-instantsearch';
import debounce from 'lodash.debounce';
import track from '@helper/tracking';
import CloseIcon from '../../icons/close/close';
import { ArrowRight, ArrowLeft } from '../../icons/arrow/arrow';

import { StyledSearchBar } from './searchBar.style';
import Container from '../../layout/container/container';
import SearchTeaser from '../../molecules/search-teaser/searchTeaser';
import StyledPagination from '../pagination/pagination.style';

const typesenseInstantSearchAdapter = new TypesenseInstantsearchAdapter({
  server: {
    apiKey: process.env.NEXT_PUBLIC_TYPESENSE_API_KEY,
    nodes: [
      {
        host: process.env.NEXT_PUBLIC_TYPESENSE_HOST,
        port: process.env.NEXT_PUBLIC_TYPESENSE_PORT,
        protocol: process.env.NEXT_PUBLIC_TYPESENSE_PROTOCOL
      }
    ]
  },
  additionalSearchParameters: {
    query_by: 'title,headline,subline,introCopy',
    query_by_weights: '6,5,3,1',
    filter_by: `event_start_timestamp:=0 || event_start_timestamp:>=${Math.floor(Date.now() / 1000)}`
  }
});

const debouncedTrack = debounce(searchValue => {
  track(['trackSiteSearch', searchValue, false, false]);
}, 250);

const SearchBox = () => {
  const t = useTranslations();
  const { query, refine } = useSearchBox();

  const handleChange = event => {
    refine(event.currentTarget.value);
    debouncedTrack(event.currentTarget.value);
  };

  const handleClear = () => {
    debouncedTrack('');
    refine('');
  };

  return (
    <form
      noValidate
      action=""
      role="search"
      className="searchBar__input"
      onSubmit={e => e.preventDefault()}
    >
      <label className="sr-only" htmlFor="search-input">
        {t('search.input')}
      </label>
      <input
        autoFocus
        id="search-input"
        type="search"
        placeholder={t('search.input')}
        value={query}
        onChange={handleChange}
      />
      <button type="button" onClick={handleClear}>
        <CloseIcon />
        <span className="sr-only">{t('search.clear')}</span>
      </button>
    </form>
  );
};

const StateResults = () => {
  const t = useTranslations();
  const { results } = useInstantSearch();
  const hasResults = results && results.nbHits !== 0;

  return (
    <div hidden={hasResults} className="searchBar__no-results">
      <p>{t('search.noResults')}</p>
    </div>
  );
};

const PaginationLink = ({ children, linkTo, currentRefinement, refine, createURL }) => {
  const t = useTranslations();

  const handleClick = event => {
    event.preventDefault();
    refine(linkTo);
  };

  const ariaLabel =
    currentRefinement === linkTo
      ? `${t('pagination.currentPage')} ${linkTo}`
      : `${t('pagination.goTo')} ${linkTo}`;

  return (
    <a
      aria-label={ariaLabel}
      aria-current={currentRefinement === linkTo}
      href={createURL(linkTo)}
      onClick={handleClick}
      className="pagination__link"
    >
      <div className="pagination__link-inner">
        <span>{children}</span>
      </div>
    </a>
  );
};

const CPagination = () => {
  const { currentRefinement, nbPages, refine, createURL } = usePagination();
  const links = [];
  const startIndex = currentRefinement >= 2 ? currentRefinement - 1 : 1;
  const paginationLinkProps = { currentRefinement, refine, createURL };

  // usePagination is 0-based, convert to 1-based for display
  const current = currentRefinement + 1;
  const total = nbPages;

  for (let i = startIndex; i <= total && i <= current + 1; i += 1) {
    links.push(
      <li key={i}>
        <PaginationLink linkTo={i - 1} {...paginationLinkProps}>
          {i}
        </PaginationLink>
      </li>
    );
  }

  if (total <= 1) return null;

  return (
    <StyledPagination inverted>
      <ul>
        {current !== 1 && (
          <li className="pagination__next">
            <PaginationLink linkTo={currentRefinement - 1} {...paginationLinkProps}>
              <ArrowLeft />
            </PaginationLink>
          </li>
        )}
        {links}
        {current + 2 <= total && (
          <>
            {total !== 3 && current + 2 < total && <span className="pagination__spacer">...</span>}
            <li>
              <PaginationLink linkTo={total - 1} {...paginationLinkProps}>
                {total}
              </PaginationLink>
            </li>
          </>
        )}
        {current !== total && (
          <li className="pagination__next">
            <PaginationLink linkTo={currentRefinement + 1} {...paginationLinkProps}>
              <ArrowRight />
            </PaginationLink>
          </li>
        )}
      </ul>
    </StyledPagination>
  );
};

const Hit = ({ hit }) => (
  <div>
    <SearchTeaser
      uri={hit.uri}
      headline={hit.headline}
      subline={hit.subline}
      className="searchBar__teaser"
    />
  </div>
);

Hit.propTypes = {
  hit: PropTypes.shape({
    uri: PropTypes.string.isRequired,
    headline: PropTypes.string.isRequired,
    subline: PropTypes.string.isRequired,
    introCopy: PropTypes.string.isRequired
  }).isRequired
};

export const SearchBar = () => {
  const locale = useLocale();
  const searchModal = React.useRef();
  const indexName = locale === 'en' ? 'futurium_combined_en' : 'futurium_combined_de';

  return (
    <StyledSearchBar innerRef={searchModal}>
      <InstantSearch
        indexName={indexName}
        searchClient={typesenseInstantSearchAdapter.searchClient}
      >
        <Configure hitsPerPage={9} />
        <Container>
          <div className="searchBar__input-wrapper">
            <SearchBox />
          </div>
        </Container>
        <div className="searchBar__results">
          <Hits hitComponent={Hit} />
          <CPagination />
          <StateResults />
        </div>
      </InstantSearch>
    </StyledSearchBar>
  );
};

export default SearchBar;
